import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { 
  Plus, 
  Trash2, 
  Edit,
  X,
  Check,
  Package,
  Search,
  Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'ro', name: 'RO', flag: '🇷🇴' },
  { code: 'en', name: 'EN', flag: '🇬🇧' },
  { code: 'de', name: 'DE', flag: '🇩🇪' }
];

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState(null);
  const [selectedLang, setSelectedLang] = useState('ro');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    descriptions: { ro: '', en: '', de: '' },
    price: '',
    category: 'wellness',
    image_url: '',
    qv_points: '',
    bv_points: '',
    stock: '100',
    affiliate_link: ''
  });

  const categories = ['wellness', 'skincare', 'energy', 'sleep', 'pain-relief', 'bundles', 'enrollment packs', 'equine'];

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/products`);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    const productData = {
      name: formData.name,
      description: formData.description,
      descriptions: formData.descriptions,
      price: parseFloat(formData.price),
      category: formData.category,
      image_url: formData.image_url,
      pv_points: parseFloat(formData.qv_points) || 0,
      bv_points: parseFloat(formData.bv_points) || 0,
      stock: parseInt(formData.stock) || 100
    };

    try {
      if (editingProduct) {
        await axios.put(`${API_URL}/api/admin/products/${editingProduct.id}`, {
          name: formData.name,
          description: formData.description,
          descriptions: formData.descriptions,
          price: parseFloat(formData.price),
          category: formData.category,
          image_url: formData.image_url,
          qv_points: parseFloat(formData.qv_points) || 0,
          bv_points: parseFloat(formData.bv_points) || 0,
          stock: parseInt(formData.stock) || 100,
          affiliate_link: formData.affiliate_link || null
        });
        setMessage({ type: 'success', text: 'Produs actualizat!' });
      } else {
        await axios.post(`${API_URL}/api/admin/products`, productData);
        setMessage({ type: 'success', text: 'Produs adăugat!' });
      }
      
      resetForm();
      fetchProducts();
    } catch (error) {
      setMessage({ type: 'error', text: 'Eroare la salvarea produsului.' });
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      descriptions: product.descriptions || { ro: '', en: product.description, es: '', it: '', de: '' },
      price: product.price.toString(),
      category: product.category,
      image_url: product.image_url,
      qv_points: (product.qv_points || '').toString(),
      bv_points: (product.bv_points || '').toString(),
      stock: (product.stock || 100).toString(),
      affiliate_link: product.affiliate_link || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Sigur vrei să ștergi acest produs?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/products/${productId}`);
      setMessage({ type: 'success', text: 'Produs șters!' });
      fetchProducts();
    } catch (error) {
      setMessage({ type: 'error', text: 'Eroare la ștergerea produsului.' });
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    setSelectedLang('ro');
    setFormData({
      name: '',
      description: '',
      descriptions: { ro: '', en: '', de: '' },
      price: '',
      category: 'wellness',
      image_url: '',
      qv_points: '',
      bv_points: '',
      stock: '100',
      affiliate_link: ''
    });
  };

  const updateDescription = (lang, value) => {
    setFormData(prev => ({
      ...prev,
      descriptions: {
        ...prev.descriptions,
        [lang]: value
      },
      // Also update main description with English
      description: lang === 'en' ? value : prev.description
    }));
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Produse</h1>
          <p className="text-slate-400 mt-1">{products.length} produse în catalog</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-sky-500 hover:bg-sky-600 text-white rounded-xl"
        >
          <Plus className="w-4 h-4 mr-2" />
          Adaugă Produs
        </Button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-xl flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
            : 'bg-red-500/20 text-red-400 border border-red-500/50'
        }`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder="Caută produse..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50"
        />
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
          <h2 className="text-xl font-bold text-white mb-4">
            {editingProduct ? 'Editează Produs' : 'Produs Nou'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Nume Produs *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Categorie *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Multi-language Description */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Descriere (Traduceri)
              </label>
              
              {/* Language tabs */}
              <div className="flex gap-1 mb-2">
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => setSelectedLang(lang.code)}
                    className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 transition-colors ${
                      selectedLang === lang.code
                        ? 'bg-sky-500 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.name}</span>
                  </button>
                ))}
              </div>
              
              <textarea
                value={formData.descriptions[selectedLang] || ''}
                onChange={(e) => updateDescription(selectedLang, e.target.value)}
                rows={3}
                placeholder={`Descriere în ${LANGUAGES.find(l => l.code === selectedLang)?.name}...`}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500/50 resize-none"
              />
              <p className="text-slate-500 text-xs mt-1">
                💡 Completează descrierea pentru fiecare limbă. Dacă lași o limbă goală, se folosește engleza.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Preț (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  required
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">QV Points</label>
                <input
                  type="number"
                  value={formData.qv_points}
                  onChange={(e) => setFormData({...formData, qv_points: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">BV Points</label>
                <input
                  type="number"
                  value={formData.bv_points}
                  onChange={(e) => setFormData({...formData, bv_points: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">URL Imagine *</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                required
                placeholder="https://..."
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Link Afiliat (opțional)</label>
              <input
                type="url"
                value={formData.affiliate_link}
                onChange={(e) => setFormData({...formData, affiliate_link: e.target.value})}
                placeholder="https://lifewave.com/ioanaalexandra/store/product/..."
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500/50"
              />
              <p className="text-slate-500 text-xs mt-1">
                💡 Dacă adaugi un link specific, butonul "Cumpără" va deschide direct pagina acestui produs.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" className="bg-sky-500 hover:bg-sky-600 text-white rounded-xl">
                {editingProduct ? 'Salvează Modificările' : 'Adaugă Produs'}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm} className="rounded-xl border-slate-600 text-slate-300 hover:bg-slate-700">
                Anulează
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredProducts.map((product) => (
          <div key={product.id} className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden group">
            <div className="aspect-square bg-slate-700 relative">
              <img 
                src={product.image_url} 
                alt={product.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button
                  onClick={() => handleEdit(product)}
                  className="bg-sky-500 hover:bg-sky-600 text-white rounded-full w-10 h-10 p-0"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => handleDelete(product.id)}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full w-10 h-10 p-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              {product.descriptions && Object.keys(product.descriptions).some(k => product.descriptions[k]) && (
                <div className="absolute top-2 right-2 px-2 py-1 bg-green-500/80 text-white text-xs rounded-full flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  Tradus
                </div>
              )}
            </div>
            <div className="p-4">
              <p className="text-xs text-sky-400 uppercase mb-1">{product.category}</p>
              <h3 className="font-bold text-white mb-1 truncate">{product.name}</h3>
              <p className="text-slate-400 text-sm line-clamp-2 mb-2">{product.description}</p>
              <div className="flex items-center justify-between">
                <p className="text-xl font-bold text-white">€{product.price}</p>
                <p className="text-xs text-slate-500">QV: {product.qv_points || 0}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredProducts.length === 0 && !loading && (
        <div className="text-center py-12 bg-slate-800 rounded-2xl border border-slate-700">
          <Package className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">Nu am găsit produse</p>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
